<template>
  <li
    class="text-small-1 free-plan-link pointer py-2 d-flex align-items-center radial-hover"
    @click="onClick"
    v-if="show && subscription && isBillingAllowed"
  >
    <span class="icon-wrapper flex-center mr-2">
      <i class="arrow-up-icon"></i>
    </span>
    <span class="bold text-secondary">{{ getPlanDisplayName(subscription.name) }}</span>
    <alert title="Upgrade to a higher plan" ref="alert" ok-variant="primary">
      <div class="mb-4">
        You're currently on a free plan. Contact
        {{ orgOwners.length === 1 ? 'org owner' : 'any of your org owners' }}
        below to upgrade to a higher plan
      </div>
      <div v-for="owner in orgOwners" :key="owner.userId" class="pb-2">
        <user-short
          :user-id="owner.userId"
          :show-user-name="true"
          :length="25"
          :is-name-bold="true"
        />
      </div>
      <div class="mt-2">
        See
        <b-link
          href="https://thenorth.io/pricing/"
          target="_blank"
          rel="nofollow noopener noreferrer"
          >plans and pricing</b-link
        >
      </div>
    </alert>
  </li>
</template>

<script>
import Alert from '@/components/Alert'
import UserShort from '@/components/UserShort'
import { ADMIN_PANEL_TAB_ID } from '@/constants/adminPanel'

/**
 * Shows a link if user is on a free plan
 */
import BillingMixin from '@/mixins/BillingMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import getPlanDisplayName from '@/utils/getPlanDisplayName'
import handleClickNavigation from '@/utils/handleClickNavigation'
import { getAdminPageRouteConfig } from '@/utils/navigationConfigs'
import { BLink } from 'bootstrap-vue'
import { featureFlags } from '@/constants/featureFlags'
import FeaturesMixin from '@/mixins/FeaturesMixin'

export default {
  name: 'FreePlanLink',
  props: ['orgId', 'subOrgId'],
  mixins: [BillingMixin, MembershipsMixin, FeaturesMixin],
  components: {
    Alert,
    BLink,
    UserShort
  },
  data() {
    return {
      show: false,
      subscription: null
    }
  },
  computed: {
    user() {
      return this.$store.getters.user
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    isUserOrgAdmin() {
      return this.org.memberships?.some(
        user => user.type === 'owner' && user.userId === this.user.id
      )
    },
    orgOwners() {
      return this.org.memberships?.filter(user => user.type === 'owner') || []
    },
    isBillingAllowed() {
      return this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_BILLING)
    }
  },
  methods: {
    getPlanDisplayName,
    onClick(event) {
      if (this.isUserOrgAdmin) {
        const plansPageLocation = getAdminPageRouteConfig({
          orgId: this.orgId,
          subOrgId: this.subOrgId,
          query: {
            section: ADMIN_PANEL_TAB_ID.PLANS_TAB_ID
          }
        })
        handleClickNavigation.call(this, event, plansPageLocation)
      } else {
        this.$refs.alert.show()
      }
    },
    async handleVisibility() {
      this.subscription = await this.fetchActiveSubscription(this.orgId)
      this.show = this.isFreeSubscription(this.subscription)
      if (!this.org.memberships) {
        this.updateMemberships(this.orgId)
      }
    }
  },
  async mounted() {
    this.handleVisibility()
  },
  watch: {
    orgId: 'handleVisibility'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.free-plan-link {
  border-bottom: 1px solid $borderSecondary;
}
.icon-wrapper {
  @include dimension(15px, 15px);

  border: 1px solid $borderPrimary;
  border-radius: 50%;
  flex-shrink: 0;
  i {
    @include dimension(8px, 8px);
    background-size: contain;
  }
}
</style>
